import React, { useEffect, useState } from 'react';
import { Mail, ArrowLeft, CheckCircle } from 'lucide-react';
import { HitApi, HitPublicApi } from '../../Api/ApiHit';
import { forgotPassword } from '../../constant/Constant';
import logo from '../../images/Elevate.png';
import background from '../../images/login.jpg';
import { useNavigate } from 'react-router-dom';

const ForgotPassword = () => {
  const [email, setEmail] = useState('');
  const [errors, setErrors] = useState({
    email: '',
    general: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    document.body.style.overflow = 'hidden';
    document.body.style.height = '100vh';
    document.documentElement.style.overflow = 'hidden';
    document.documentElement.style.height = '100vh';

    return () => {
      document.body.style.overflow = '';
      document.body.style.height = '';
      document.documentElement.style.overflow = '';
      document.documentElement.style.height = '';
    };
  }, []);

  const validateForm = () => {
    let valid = true;
    const newErrors = {
      email: '',
      general: ''
    };

    if (!email.trim()) {
      newErrors.email = 'Email is required';
      valid = false;
    } else if (!/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i.test(email)) {
      newErrors.email = 'Please enter a valid email address';
      valid = false;
    }

    setErrors(newErrors);
    return valid;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (validateForm()) {
      setIsSubmitting(true);
      setErrors({ email: '', general: '' });

      try {
        const response = await HitApi({ email: email.trim() }, forgotPassword);

        if (response?.statusCode === 200) {
          setIsSuccess(true);
          setTimeout(() => {
            navigate('/reset-password', { state: { email: email.trim() } });
          }, 2000);
        } else {
          setErrors({
            email: '',
            general: response?.message || 'Failed to send reset link. Please try again.'
          });
        }
      } catch (error) {
        console.error('Forgot password error:', error);
        setErrors({
          email: '',
          general: 'An unexpected error occurred. Please try again later.'
        });
      } finally {
        setIsSubmitting(false);
      }
    }
  };

  const handleBackToLogin = () => {
    navigate('/login');
  };

  if (isSuccess) {
    return (
      <div className="fixed inset-0 flex items-center justify-end p-4 sm:p-20 overflow-hidden">
        <div
          className="absolute inset-0 bg-cover bg-center bg-no-repeat"
          style={{
            backgroundImage: `url(${background})`,
          }}
        >
          <div className="h-full w-full" style={{ background: "rgba(0, 0, 0, 0.6)" }}></div>
        </div>

        <div className="relative z-20 max-w-xl w-full max-h-screen overflow-y-auto px-6 sm:px-10 py-8 sm:py-12 mx-4 bg-white bg-opacity-98 backdrop-blur-lg rounded-3xl shadow-2xl border border-gray-100">
          <div className="text-center">
            <div className="mb-6">
              <img src={logo} alt="Elevate Logo" className="h-16 sm:h-20 w-auto mx-auto mb-4" />
            </div>

            <div className="flex justify-center mb-6">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center">
                <CheckCircle className="h-10 w-10 text-green-600" />
              </div>
            </div>

            <h2 className="text-2xl font-bold text-gray-800 mb-4">Check Your Email</h2>
            <p className="text-gray-600 mb-8">
              We've sent a password reset link to <strong>{email}</strong>.
              Please check your email and follow the instructions.
            </p>

            <div className="text-sm text-gray-500">
              Redirecting to reset password page...
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 flex items-center justify-end p-4 sm:p-20 overflow-hidden">
      <div
        className="absolute inset-0 bg-cover bg-center bg-no-repeat"
        style={{
          backgroundImage: `url(${background})`,
        }}
      >
        <div className="h-full w-full" style={{ background: "rgba(0, 0, 0, 0.6)" }}></div>
      </div>

      <div className="relative z-20 max-w-xl w-full max-h-screen overflow-y-auto px-6 sm:px-10 py-8 sm:py-12 mx-4 bg-white bg-opacity-98 backdrop-blur-lg rounded-3xl shadow-2xl border border-gray-100">
        <div className="flex flex-col items-center mb-2">
          <div className="mb-6 text-center">
            <img src={logo} alt="Elevate Logo" className="h-16 sm:h-20 w-auto mx-auto mb-4" />
            <h2 className="text-2xl font-bold text-gray-800 mb-2">Forgot Password?</h2>
            <p className="text-sm text-gray-600">Enter your email to receive a reset link</p>
            <div className="w-full h-1 bg-gradient-to-r from-yellow-400 to-yellow-600 rounded-full mx-auto mt-3"></div>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4 sm:space-y-6">
          {errors.general && (
            <div className="p-3 sm:p-4 bg-red-50 border-l-4 border-red-400 text-red-800 rounded-lg">
              <div className="flex items-center">
                <svg className="w-5 h-5 text-red-400 mr-3 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                </svg>
                <span className="font-medium text-sm">{errors.general}</span>
              </div>
            </div>
          )}

          <div className="space-y-2">
            <label htmlFor="email" className="block text-sm font-semibold text-gray-700">
              Email Address <span className="text-red-500">*</span>
            </label>
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 sm:pl-4 flex items-center pointer-events-none">
                <Mail className="h-5 w-5 text-gray-400" />
              </div>
              <input
                id="email"
                name="email"
                type="email"
                autoComplete="email"
                className={`w-full pl-10 sm:pl-12 pr-4 py-3 sm:py-4 border-2 rounded-xl focus:outline-none focus:ring-2 focus:ring-yellow-500 focus:border-yellow-500 transition-all duration-300 text-gray-800 font-medium ${errors.email ? 'border-red-400 bg-red-50' : 'border-gray-200 bg-gray-50 hover:bg-white hover:border-gray-300'
                  }`}
                placeholder="Enter your email address"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                disabled={isSubmitting}
              />
            </div>
            {errors.email && (
              <p className="text-sm text-red-500 flex items-center font-medium">
                <svg className="w-4 h-4 mr-2 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                </svg>
                {errors.email}
              </p>
            )}
          </div>

          <button
            type="submit"
            className="w-full flex justify-center items-center py-3 sm:py-4 px-6 text-base sm:text-lg font-bold text-white bg-gradient-to-r from-gray-800 to-black hover:from-gray-900 hover:to-gray-800 focus:outline-none focus:ring-4 focus:ring-gray-300 disabled:opacity-50 disabled:cursor-not-allowed rounded-xl transition-all duration-300 transform hover:scale-[1.02] active:scale-[0.98] shadow-lg hover:shadow-xl"
            disabled={isSubmitting}
          >
            {isSubmitting ? (
              <>
                <svg className="animate-spin -ml-1 mr-3 h-5 sm:h-6 w-5 sm:w-6 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                Sending...
              </>
            ) : (
              <>
                <Mail className="h-5 w-5 mr-2" />
                Send Reset Link
              </>
            )}
          </button>

          <button
            type="button"
            onClick={handleBackToLogin}
            className="w-full flex justify-center items-center py-3 sm:py-4 px-6 text-base sm:text-lg font-semibold text-gray-700 bg-gray-100 hover:bg-gray-200 focus:outline-none focus:ring-4 focus:ring-gray-300 rounded-xl transition-all duration-300"
            disabled={isSubmitting}
          >
            <ArrowLeft className="h-5 w-5 mr-2" />
            Back to Login
          </button>
        </form>
      </div>
    </div>
  );
};

export default ForgotPassword;
